<!--
  Header whatsapp button: the last conversations in a dropdown - whatsapp AND mail threads, the
  ones /messages holds (the header's envelope, which only went to /messages, was folded into
  this box on 15/10/2026). A row opens the conversation in the app-wide chat modal (the same one
  the incoming-message toast opens), and "הודעה חדשה" searches contacts to start a whatsapp
  thread with someone who never wrote in. An account whose whatsapp module is switched off
  still gets the box, as an envelope with its mail threads only.

  Mounted twice - once in the desktop icon row, once in the mobile one (both live in the DOM,
  CSS hides one), so the two instances keep their own open/unread state. Only the visible one
  is ever clicked, so that costs nothing.
-->
<template>

    <li @click="toggleBox"
        :style="mobile ? 'list-style-type:none;padding:12px;font-size:20px;position:relative;top:-2px;' : ''"
        title="הודעות"
        v-bind:class="{ highlightHeaderButton: showBox }">

        <!-- the brand glyph is drawn lighter and smaller than the fa-regular bell/calendar/clock
             beside it, so it gets a few px of size to match their presence - inline because the
             theme's `.nav-right ul li i {font-size:20px}` outranks any class we could add here.
             NOT -webkit-text-stroke: that thickens it but renders jagged at this size.
             The `top` puts its ink on the same center line as the glyphs beside it (measured
             off a 3x screenshot: without it the mobile one sat 3px above the calendar) -->
        <i v-if="!whatsappOff"
           class="fa-brands fa-whatsapp"
           :style="mobile ? 'position:relative;top:3px;font-size:23px;' : 'position:relative;top:4px;font-size:23px;'"></i>

        <i v-else
           class="fa-regular fa-envelope"
           :style="mobile ? 'position:relative;top:2px;' : 'position:relative;top:3px;'"></i>

        <span v-if="unreadCount > 0"
              class="notification-badge">{{ unreadCount }}</span>


        <div v-show="showBox"
             @click.stop
             class="card headerWhatsappCard"
             :style="mobile ? 'position:fixed;top:64px;left:8px;right:8px;width:auto;' : 'position:absolute;left:0;top:80px;width:400px;'"
             style="box-shadow:0 5px 7px 0 rgba(0,0,0,.1);border-radius:0 0 6px 6px;border:solid 1px #eeee;background:white;max-height:500px;overflow-y:auto;font-size:14px;text-align:right;direction:rtl;">


            <div
                 style="background:#eeeeee;text-align:center;font-size:18px;font-weight:600;position:relative;padding:2px 0;">

                <span v-if="mode == 'list'">הודעות אחרונות</span>
                <span v-else>הודעה חדשה</span>

                <router-link to="/messages"
                             v-if="mode == 'list'"
                             @click.native="closeBox"
                             style="position:absolute;left:10px;top:50%;transform:translateY(-50%);font-size:12px;color:#ab8ce4;">
                    הצג הכל
                </router-link>

                <span v-else
                      @click="showList"
                      style="position:absolute;left:10px;top:50%;transform:translateY(-50%);font-size:12px;color:#ab8ce4;cursor:pointer;">
                    חזרה
                </span>

            </div>


            <!-- conversations -->
            <div v-if="mode == 'list'">

                <!-- a new thread is a whatsapp one (the chat is keyed whatsapp_contact_<id>) -->
                <div v-if="!whatsappOff"
                     style="padding:10px;border-bottom:solid 1px #eeeeee;">
                    <button class="btn btn-primary"
                            style="width:100%;padding:7px;font-size:15px;"
                            @click="startNewMessage">
                        <i class="fa-solid fa-pen"
                           style="font-size:13px;"></i>
                        &nbsp;
                        הודעה חדשה
                    </button>
                </div>

                <div style="padding:15px 15px 0 15px;">

                    <div v-if="listState == 'loading'"
                         style="text-align:center;padding-bottom:15px;">
                        <i class="fa-solid fa-spinner fa-spin"></i>
                    </div>

                    <div v-if="listState == 'empty'"
                         style="text-align:center;color:#888;padding-bottom:15px;">
                        אין הודעות
                    </div>

                    <!-- v-if (not v-show) so every opening of the box refetches - the component
                         loads its messages on create. Every channel (no type) - a mail thread
                         sits among the whatsapp ones with the envelope on its avatar -->
                    <LatestPlanupMultiMessages v-if="showBox"
                                               :key="listKey"
                                               :limit="10"
                                               :type="whatsappOff ? 'email' : ''"
                                               @loaded="listLoaded"
                                               @clicked="openConversation"></LatestPlanupMultiMessages>
                </div>

            </div>


            <!-- new message: pick a contact -->
            <div v-else>

                <div class="headerWhatsappSearch"
                     style="padding:10px;">
                    <input v-model="searchTerm"
                           ref="searchInput"
                           class="form-control"
                           placeholder="חיפוש לפי שם או טלפון"
                           autocomplete="off"
                           style="text-align:right;">

                    <div v-if="!searching && suggestions.length"
                         class="headerWhatsappSuggestionsLine"
                         style="color:#999;font-size:12px;padding:6px 2px 0 2px;">
                        {{ suggestionsLine }}
                    </div>
                </div>

                <div v-if="searching"
                     style="text-align:center;padding:0 0 15px 0;">
                    <i class="fa-solid fa-spinner fa-spin"></i>
                </div>

                <div v-if="!searching && searchTerm.length > 1 && !suggestions.length"
                     style="text-align:center;color:#888;padding:0 0 15px 0;">
                    לא נמצאו אנשי קשר עם מספר טלפון
                </div>

                <!-- every match: the list scrolls, the title (חזרה) and the search box stay in view -
                     sized to keep the whole card under its max-height of 500px -->
                <div class="headerWhatsappSuggestions"
                     style="max-height:375px;overflow-y:auto;">

                    <div v-for="suggestion in suggestions"
                         :key="suggestion.id"
                         class="headerWhatsappSuggestion"
                         @click="openConversationWithContact(suggestion)">

                        <img loading="lazy"
                             :src="suggestion.pic ? 'https://planup-il.s3.eu-central-1.amazonaws.com/' + suggestion.pic : $globals.getLetterImage(suggestion.name)">

                        <span class="headerWhatsappSuggestionName">{{ suggestion.name }}</span>

                        <span class="headerWhatsappSuggestionPhone">{{ suggestion.phone }}</span>

                    </div>

                </div>

            </div>


        </div>

    </li>

</template>
<script>

import axiosV2 from '../../core/http/axios_api_v2';
import { debounce } from '../../helpers/interaction';
import { contactSuggestions, contactSuggestionsLine } from '../../helpers/contact-search';
import { featureDisabled } from '@/core/acl/features';
import LatestPlanupMultiMessages from './LatestPlanupMultiMessages.vue';

export default {

    name: 'HeaderWhatsappBox',

    props: ['mobile'],

    components: { LatestPlanupMultiMessages },

    data() {
        return {
            showBox: false,
            mode: 'list',               // 'list' = recent conversations, 'new' = contact search
            listState: 'loading',       // 'loading' / 'empty' / 'has'
            listKey: 0,                 // bumped to remount the list, which refetches it
            unreadCount: 0,
            searchTerm: '',
            suggestions: [],
            suggestionsMore: false,     // the api had more rows than it sends (search-results findAll's limit)
            searching: false,
            searchRandom: null,
        }
    },

    computed: {

        suggestionsLine() {
            return contactSuggestionsLine(this.suggestions.length, this.suggestionsMore);
        },

        //the root admin switched the whatsapp module off for this account - mail threads only
        whatsappOff() {
            return featureDisabled(this.$store, 'whatsapp');
        },

    },

    watch: {

        searchTerm() {

            this.suggestions = [];

            if (this.searchTerm.trim().length < 2) {
                this.searching = false;
                return;
            }

            this.searching = true;
            debounce(this.searchContacts, 400)();
        }

    },

    mounted() {

        //incoming whatsapp messages and mail arrive over the socket (WhatsappHandler /
        //EmailHandler) - refresh the list when the box is open, otherwise count them so the
        //badge says how many came in meanwhile
        this.$root.$on('whatsappMessageArrived', this.onMessageArrived);
        this.$root.$on('emailMessageArrived', this.onMessageArrived);

        document.addEventListener('click', this.onDocumentClick);
    },

    beforeDestroy() {

        this.$root.$off('whatsappMessageArrived', this.onMessageArrived);
        this.$root.$off('emailMessageArrived', this.onMessageArrived);

        document.removeEventListener('click', this.onDocumentClick);
    },

    methods: {

        toggleBox() {

            if (this.showBox) {
                this.closeBox();
                return;
            }

            //the bell / recent-actions dropdowns sit at the same offset and would overlap
            this.$root.$emit('closeHeaderDropdowns');

            this.showList();
            this.showBox = true;
            this.unreadCount = 0;
        },

        closeBox() {
            this.showBox = false;
        },

        showList() {
            this.mode = 'list';
            this.listState = 'loading';
            this.listKey++;
            this.searchTerm = '';
            this.suggestions = [];
            this.searching = false;
        },

        //the box is inside this <li>, and the card stops its own clicks - so anything that
        //reaches the document from outside the button closes it
        onDocumentClick(event) {

            if (!this.showBox)
                return;

            if (this.$el && this.$el.contains(event.target))
                return;

            this.closeBox();
        },

        onMessageArrived(message) {

            //an open list refetches so the new message shows up at the top right away
            if (this.showBox && this.mode == 'list') {
                this.showList();
                return;
            }

            //closed box (or one busy composing a new message) - count it into the badge
            if (message && message.incomingFromCustomer)
                this.unreadCount++;
        },

        //LatestPlanupMultiMessages emits its newest message (undefined when it found none)
        listLoaded(firstMessage) {
            this.listState = firstMessage ? 'has' : 'empty';
        },

        openConversation(message) {
            this.closeBox();
            this.$root.$emit('globalChatBox', message.msgGroup);
        },

        openConversationWithContact(contact) {
            this.closeBox();
            this.$root.$emit('globalChatBox', `whatsapp_contact_${contact.id}`);
        },

        startNewMessage() {

            this.mode = 'new';
            this.searchTerm = '';
            this.suggestions = [];

            this.$nextTick(() => { if (this.$refs.searchInput) this.$refs.searchInput.focus(); });
        },

        searchContacts() {

            let term = this.searchTerm.trim();

            // "052-247 7699" → "0522477699" so phone fragments match the digits in searchText
            if (/^[\d\s\-+]+$/.test(term))
                term = term.replace(/[\s\-+]/g, '');

            if (term.length < 2) {
                this.searching = false;
                return;
            }

            this.searchRandom = Math.random();
            let requestRandom = this.searchRandom;

            axiosV2(`/search-results/?term=${encodeURIComponent(term)}&random=${requestRandom}`).then(response => {

                if (!response.data || response.data.random != this.searchRandom)
                    return;

                //one row per contact - a customer with several events has several search_result rows,
                //and only contacts with an id AND a phone can be messaged (the conversation is keyed
                //by contact id, the send needs the number). Every one of them (it stopped at 8), the
                //ones whose name / phone matched first (contact-search.js)
                this.suggestions = contactSuggestions(response.data.results || [], response.data.words || term.split(' '), { byContactId: true });
                this.suggestionsMore = !!response.data.more;
                this.searching = false;

            }).catch(() => { this.searching = false; });
        },

    }

}

</script>
<style>
.headerWhatsappCard .multiMessageContact {
    padding-bottom: 12px;
}

.headerWhatsappSuggestion {
    display: flex;
    align-items: center;
    padding: 8px 15px;
    cursor: pointer;
}

.headerWhatsappSuggestion:hover {
    background: #f6f7fb;
}

.headerWhatsappSuggestion img {
    height: 34px;
    width: 34px;
    border-radius: 100%;
    margin-left: 10px;
    flex: none;
    border: solid 1px #eeeeee;
}

.headerWhatsappSuggestionName {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.headerWhatsappSuggestionPhone {
    color: #999;
    font-size: 13px;
    direction: ltr;
    flex: none;
}
</style>
